import type { RefObject } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { AutoScroller, Cursor, Feedback, PreventSelection } from '@dnd-kit/dom';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import HighlightAltOutlined from '@mui/icons-material/HighlightAltOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { Box, Card, Skeleton } from '@mui/material';

import presets from '@/components/Dialog/presets';
import TooltipIconButton from '@/components/TooltipIconButton';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import type { TileBigNumber } from '@/features/Dashboard/types';
import { AppType } from '@/features/Dashboard/types';
import { convertTilesToActiveSymbols } from '@/features/Dashboard/utils/tileSymbolConverter';
import { useAppSelector } from '@/hooks/redux-hooks';
import Tile from '@/pages/Markets/MarketsComponents/Tiles/Tile';
import type { TileType } from '@/pages/Markets/MarketsComponents/Tiles/types';
import type { MarketsComponentType } from '@/types/markets';

import { useDashboardEditActions } from '../../hooks/useDashboardEditActions';
import { selectCurrentTabId, selectCurrentTiles } from '../../model/dashboardEdit.selectors';
import { ConfirmationDialog } from '../ConfirmationDialog';
import MoveTileToDashboardMenu from './MoveTileToDashboardMenu';

const TileSkeleton = () => (
  <Card sx={{ height: '56px', p: 0.25 }}>
    <Skeleton
      sx={{ height: '100%' }}
      variant='rectangular'
    />
  </Card>
);

const SortableTile = ({
  tile,
  onRequestRemove,
  onRequestEdit,
  onRequestMove,
  index,
  container,
  preview
}: {
  index: number;
  tile: TileBigNumber;
  onRequestRemove: (id: string) => void;
  onRequestEdit: (id: string) => void;
  onRequestMove: (tile: TileBigNumber, anchorEl: HTMLElement) => void;
  container: RefObject<HTMLDivElement | null>;
  preview: any;
}) => {
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const { ref } = useSortable({
    id: tile.id,
    index,
    handle: handleRef,
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  const content = useMemo((): TileType | undefined => {
    const components = (preview?.components ?? []) as MarketsComponentType[];
    for (const app of components) {
      if ('component' in app && app.component?.type === 'tiles') {
        const tilesComponent = app.component as { tiles: TileType[] };
        const found = tilesComponent.tiles.find((t: TileType) => t.id === tile.id);
        if (found) return found;
      }
    }
    return undefined;
  }, [preview, tile.id]);

  return (
    <Box
      ref={ref}
      sx={{
        borderWidth: 2,
        borderStyle: 'dashed',
        borderColor: 'gx.textDisabled',
        display: 'flex',
        width: { xs: 'calc(100% + 90px)', sm: 'calc(50% + 90px)', lg: 'calc(25% + 90px)' },
        overflow: 'hidden',
        position: 'relative',
        pr: '64px',
        pl: '24px'
      }}
    >
      <TooltipIconButton
        ref={handleRef}
        sx={{
          cursor: 'grab',
          width: '24px !important',
          height: '100%',
          position: 'absolute',
          left: 0,
          top: 0
        }}
        title='Drag to reorder app'
      >
        <DragIndicatorOutlinedIcon sx={{ fontSize: '21px !important' }} />
      </TooltipIconButton>

      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {content ? (
          <>
            <Tile tile={content} />
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                zIndex: 1,
                backgroundColor: 'rgba(18, 24, 54, 0.80)'
              }}
            />
          </>
        ) : (
          <TileSkeleton />
        )}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          display: 'flex',
          right: 0,
          top: 0,
          gap: 0.25,
          zIndex: 2,
          bottom: 0,
          backgroundColor: 'background.paper'
        }}
      >
        <TooltipIconButton
          sx={{
            height: '100%'
          }}
          title='Move to ...'
          onClick={e => {
            e.stopPropagation();
            onRequestMove(tile, e.currentTarget);
          }}
        >
          <HighlightAltOutlined sx={{ fontSize: '16px' }} />
        </TooltipIconButton>

        <TooltipIconButton
          sx={{
            height: '100%'
          }}
          title='Configuration settings'
          onClick={e => {
            e.stopPropagation();
            onRequestEdit(tile.id);
          }}
        >
          <SettingsOutlined sx={{ fontSize: '16px' }} />
        </TooltipIconButton>

        <TooltipIconButton
          sx={{
            height: '100%'
          }}
          title='Remove item'
          onClick={e => {
            e.stopPropagation();
            onRequestRemove(tile.id);
          }}
        >
          <ClearOutlined
            sx={{
              fontSize: '16px'
            }}
          />
        </TooltipIconButton>
      </Box>
    </Box>
  );
};

export const TilesOrder = ({ preview }: { preview: any }) => {
  const currentTabId = useAppSelector(selectCurrentTabId);
  const tiles = useAppSelector(selectCurrentTiles);
  const actions = useDashboardEditActions();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);

  // Move menu state
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);
  const [tileToMove, setTileToMove] = useState<TileBigNumber | null>(null);

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();
  const { setInitialValues } = useAppForm();

  const requestRemove = (id: string) => {
    setPendingRemoveId(id);
    setConfirmOpen(true);
  };

  const requestEdit = useCallback(
    (id: string) => {
      const tile = tiles.find(t => t.id === id);
      if (!tile) return;

      setIsEditMode(true);
      setEditingTarget({ tileId: id });

      // Load existing tiles as active symbols
      const toActive = convertTilesToActiveSymbols(tiles);

      setInitialValues({
        type: AppType.Tiles,
        symbols: { active: toActive, backlog: [] }
      });

      setDrawerStep(DrawerStep.SelectHighlightedPrice);
    },
    [tiles, setEditingTarget, setIsEditMode, setInitialValues, setDrawerStep]
  );

  const requestMove = useCallback((tile: TileBigNumber, anchorEl: HTMLElement) => {
    setTileToMove(tile);
    setMoveMenuAnchor(anchorEl);
    setMoveMenuOpen(true);
  }, []);

  const closeMoveMenu = useCallback(() => {
    setMoveMenuOpen(false);
    setMoveMenuAnchor(null);
    setTileToMove(null);
  }, []);

  const handleRemoveTileFromCurrent = useCallback(() => {
    if (tileToMove && currentTabId) {
      actions.removeTile(currentTabId, tileToMove.id);
    }
    closeMoveMenu();
  }, [tileToMove, currentTabId, actions, closeMoveMenu]);

  const handleConfirmRemove = useCallback(() => {
    if (pendingRemoveId && currentTabId) {
      actions.removeTile(currentTabId, pendingRemoveId);
      setPendingRemoveId(null);
    }
    setConfirmOpen(false);
  }, [pendingRemoveId, currentTabId, actions]);

  const container = useRef<HTMLDivElement | null>(null);
  return (
    <DragDropProvider
      plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
      onDragEnd={event => {
        const reordered = move(tiles, event);
        actions.updateCurrentTabTiles(reordered);
      }}
    >
      <Box
        ref={container}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        {tiles.map((tile, index) => (
          <SortableTile
            key={tile.id}
            container={container}
            index={index}
            preview={preview}
            tile={tile}
            onRequestEdit={requestEdit}
            onRequestMove={requestMove}
            onRequestRemove={requestRemove}
          />
        ))}

        <ConfirmationDialog
          open={confirmOpen}
          {...presets.removeWidget}
          onCancel={() => {
            setConfirmOpen(false);
          }}
          onConfirm={handleConfirmRemove}
        />

        <MoveTileToDashboardMenu
          anchorEl={moveMenuAnchor}
          open={moveMenuOpen}
          tile={tileToMove!}
          onClose={closeMoveMenu}
          onRemoveFromCurrent={handleRemoveTileFromCurrent}
        />
      </Box>
    </DragDropProvider>
  );
};
